/**
 * React Query hooks for playground session data management
 *
 * Provides hooks for fetching and mutating playground session data with
 * automatic cache invalidation.
 *
 * Refactored to use Session-based model (no project_id required).
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { playgroundService } from '../services/playgroundApi';
import {
  PlaygroundSession,
  PlaygroundSessionCreate,
  PlaygroundSessionListResponse,
  PlaygroundSessionFilters,
} from '../types/playground';

const PLAYGROUND_SESSIONS_QUERY_KEY = 'playground-sessions';
const PLAYGROUND_SESSION_QUERY_KEY = 'playground-session';

/**
 * Hook to fetch playground session history (top-level, no project_id)
 */
export function usePlaygroundSessions(
  filters?: PlaygroundSessionFilters
): UseQueryResult<PlaygroundSessionListResponse, Error> {
  return useQuery({
    queryKey: [PLAYGROUND_SESSIONS_QUERY_KEY, filters],
    queryFn: () => playgroundService.getSessionHistory(filters),
    staleTime: 30000, // Keep data fresh for 30 seconds
  });
}

/**
 * Hook to fetch a single playground session by ID
 */
export function usePlaygroundSession(
  sessionId: string | undefined
): UseQueryResult<PlaygroundSession, Error> {
  return useQuery({
    queryKey: [PLAYGROUND_SESSION_QUERY_KEY, sessionId],
    queryFn: () => playgroundService.getSessionDetails(sessionId!),
    enabled: !!sessionId, // Only run query if sessionId is provided
    staleTime: 0, // Don't cache for running sessions - always refetch
  });
}

/**
 * Hook to create a new playground session (no project_id required)
 */
export function useCreatePlaygroundSession(): UseMutationResult<
  PlaygroundSession,
  Error,
  PlaygroundSessionCreate
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PlaygroundSessionCreate) => playgroundService.createSession(data),
    onSuccess: () => {
      // Invalidate and refetch all playground sessions
      queryClient.invalidateQueries({
        queryKey: [PLAYGROUND_SESSIONS_QUERY_KEY]
      });
    },
  });
}

/**
 * Hook to search playground sessions by message content (no project_id)
 */
export function useSearchPlaygroundSessions(
  searchQuery: string,
  filters?: Omit<PlaygroundSessionFilters, 'search'>
): UseQueryResult<PlaygroundSessionListResponse, Error> {
  return useQuery({
    queryKey: [PLAYGROUND_SESSIONS_QUERY_KEY, 'search', searchQuery, filters],
    queryFn: () => playgroundService.searchSessions(searchQuery, filters),
    enabled: !!searchQuery, // Only run if searchQuery is provided
    staleTime: 30000,
  });
}
